/**
 * useProjectionForm Hook
 * Manages form state, validation, and submission for box office projection forms
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  validateFilmId,
  validateProjectionDate,
  validateCurrencyAmount,
  validateNotes
} from '../types/boxOfficeTypes';

/**
 * Custom hook for managing projection form state and validation
 * @param {Object} initialData - Initial form data
 * @param {boolean} isEditMode - Whether form is in edit mode
 * @returns {Object} - Form state and methods
 */
export const useProjectionForm = (initialData = {}, isEditMode = false) => {
  // Form data state
  const [formData, setFormData] = useState({
    filmId: initialData.filmId || '',
    projectionDate: initialData.projectionDate || new Date().toISOString().split('T')[0],
    outlierMinimum: initialData.outlierMinimum || '',
    actualProjection: initialData.actualProjection || '',
    outlierCeiling: initialData.outlierCeiling || '',
    notes: initialData.notes || ''
  });

  // Form state
  const [formState, setFormState] = useState({
    isValid: false,
    isDirty: false,
    isSubmitting: false,
    errors: {},
    touched: {}
  });

  /**
   * Update form data
   * @param {string} field - Field name
   * @param {any} value - Field value
   */
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    setFormState(prev => ({
      ...prev,
      isDirty: true,
      touched: {
        ...prev.touched,
        [field]: true
      }
    }));
  }, []);

  /**
   * Update multiple fields at once
   * @param {Object} updates - Field updates
   */
  const updateFields = useCallback((updates) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));

    setFormState(prev => ({
      ...prev,
      isDirty: true,
      touched: {
        ...prev.touched,
        ...Object.keys(updates).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      }
    }));
  }, []);

  /**
   * Validate individual field
   * @param {string} field - Field name
   * @param {any} value - Field value
   * @returns {Object} - Validation result
   */
  const validateField = useCallback((field, value) => {
    switch (field) {
      case 'filmId':
        return validateFilmId(value);
      
      case 'projectionDate':
        return validateProjectionDate(value);
      
      case 'outlierMinimum':
        return validateCurrencyAmount(value, 'Outlier minimum');
      
      case 'actualProjection':
        return validateCurrencyAmount(value, 'Actual projection');
      
      case 'outlierCeiling':
        return validateCurrencyAmount(value, 'Outlier ceiling');
      
      case 'notes':
        return validateNotes(value);
      
      default:
        return { isValid: true, error: null };
    }
  }, []);

  /**
   * Validate entire form
   * @returns {Object} - Validation result
   */
  const validateForm = useCallback(() => {
    const errors = {};
    let isValid = true;

    // Validate each field
    Object.keys(formData).forEach(field => {
      const validation = validateField(field, formData[field]);
      if (!validation.isValid) {
        errors[field] = validation.error;
        isValid = false;
      }
    });

    // Validate logical relationships
    if (formData.outlierMinimum && formData.actualProjection && formData.outlierCeiling) {
      const outlierMin = parseFloat(formData.outlierMinimum);
      const actual = parseFloat(formData.actualProjection);
      const outlierMax = parseFloat(formData.outlierCeiling);

      if (!isNaN(outlierMin) && !isNaN(actual) && !isNaN(outlierMax)) {
        if (outlierMin > actual) {
          errors.outlierMinimum = 'Outlier minimum cannot be greater than actual projection';
          isValid = false;
        }

        if (actual > outlierMax) {
          errors.actualProjection = 'Actual projection cannot be greater than outlier ceiling';
          isValid = false;
        }

        if (outlierMin > outlierMax) {
          errors.outlierMinimum = 'Outlier minimum cannot be greater than outlier ceiling';
          isValid = false;
        }
      }
    }

    setFormState(prev => ({
      ...prev,
      isValid,
      errors
    }));

    return { isValid, errors };
  }, [formData, validateField]);

  /**
   * Validate specific field and update errors
   * @param {string} field - Field name
   */
  const validateFieldAndUpdate = useCallback((field) => {
    const validation = validateField(field, formData[field]);
    
    setFormState(prev => ({
      ...prev,
      errors: {
        ...prev.errors,
        [field]: validation.isValid ? null : validation.error
      }
    }));

    return validation;
  }, [formData, validateField]);

  /**
   * Clear field error
   * @param {string} field - Field name
   */
  const clearFieldError = useCallback((field) => {
    setFormState(prev => ({
      ...prev,
      errors: {
        ...prev.errors,
        [field]: null
      }
    }));
  }, []);

  /**
   * Clear all errors
   */
  const clearErrors = useCallback(() => {
    setFormState(prev => ({
      ...prev,
      errors: {}
    }));
  }, []);

  /**
   * Reset form to initial state
   * This should be called explicitly when the modal opens or when switching between edit/create modes
   */
  const resetForm = useCallback(() => {
    setFormData({
      filmId: initialData.filmId || '',
      projectionDate: initialData.projectionDate || new Date().toISOString().split('T')[0],
      outlierMinimum: initialData.outlierMinimum || '',
      actualProjection: initialData.actualProjection || '',
      outlierCeiling: initialData.outlierCeiling || '',
      notes: initialData.notes || ''
    });

    setFormState({
      isValid: false,
      isDirty: false,
      isSubmitting: false,
      errors: {},
      touched: {}
    });
  }, [initialData]);

  /**
   * Set form as submitting
   * @param {boolean} submitting - Whether form is submitting
   */
  const setSubmitting = useCallback((submitting) => {
    setFormState(prev => ({
      ...prev,
      isSubmitting: submitting
    }));
  }, []);

  /**
   * Get form data for API submission
   * @returns {Object} - Formatted form data
   */
  const getSubmissionData = useCallback(() => {
    // Ensure projectionDate is in YYYY-MM-DD format (not ISO datetime)
    let projectionDate = formData.projectionDate;
    if (projectionDate && projectionDate.includes('T')) {
      // If it's an ISO datetime, extract just the date part
      projectionDate = projectionDate.split('T')[0];
    }
    
    const submissionData = {
      filmId: formData.filmId,
      projectionDate: projectionDate,
      outlierMinimum: parseFloat(formData.outlierMinimum) || 0,
      actualProjection: parseFloat(formData.actualProjection) || 0,
      outlierCeiling: parseFloat(formData.outlierCeiling) || 0
    };
    
    // Only include notes if it has a value
    if (formData.notes && formData.notes.trim()) {
      submissionData.notes = formData.notes.trim();
    }
    
    return submissionData;
  }, [formData]);

  /**
   * Check if field has error
   * @param {string} field - Field name
   * @returns {boolean} - Whether field has error
   */
  const hasFieldError = useCallback((field) => {
    return !!(formState.errors[field] && formState.touched[field]);
  }, [formState.errors, formState.touched]);

  /**
   * Get field error message
   * @param {string} field - Field name
   * @returns {string|null} - Error message or null
   */
  const getFieldError = useCallback((field) => {
    return formState.touched[field] ? formState.errors[field] : null;
  }, [formState.errors, formState.touched]);

  /**
   * Get all errors (including untouched fields) - for form status display
   * @returns {Object} - All validation errors
   */
  const getAllErrors = useCallback(() => {
    return formState.errors;
  }, [formState.errors]);

  /**
   * Get touched errors only - for field-level display
   * @returns {Object} - Only errors for touched fields
   */
  const getTouchedErrors = useCallback(() => {
    const touchedErrors = {};
    Object.keys(formState.errors).forEach(field => {
      if (formState.touched[field] && formState.errors[field]) {
        touchedErrors[field] = formState.errors[field];
      }
    });
    return touchedErrors;
  }, [formState.errors, formState.touched]);

  /**
   * Check if field is touched
   * @param {string} field - Field name
   * @returns {boolean} - Whether field is touched
   */
  const isFieldTouched = useCallback((field) => {
    return !!formState.touched[field];
  }, [formState.touched]);

  // Only validate when explicitly called (e.g., on form submission)
  // Removed automatic validation to improve user experience

  // Initialize form data only once on mount - don't auto-update on initialData changes
  // This prevents the form from resetting when the user is typing
  // The form will be explicitly reset via resetForm() when the modal opens

  return {
    // Form data
    formData,
    
    // Form state
    isValid: formState.isValid,
    isDirty: formState.isDirty,
    isSubmitting: formState.isSubmitting,
    errors: formState.errors,
    touched: formState.touched,
    
    // Methods
    updateField,
    updateFields,
    validateField: validateFieldAndUpdate,
    clearFieldError,
    clearErrors,
    resetForm,
    setSubmitting,
    getSubmissionData,
    
    // Field helpers
    hasFieldError,
    getFieldError,
    isFieldTouched,
    getAllErrors,
    getTouchedErrors,
    
    // Validation
    validateForm
  };
};

export default useProjectionForm;
